import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

export type UiAvatarSize = 'sm' | 'md' | 'lg' | 'xl';
export type UiAvatarShape = 'circle' | 'square';
export type UiAvatarTone = 'neutral' | 'primary' | 'accent';

/** Prioridad de contenido: imagen → icono → iniciales (label). Inspirado en PrimeReact Avatar. */
function initialsFromLabel(text: string): string {
  const t = text.trim();
  if (!t) return '?';
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return t.length <= 2 ? t.toUpperCase() : t.slice(0, 2).toUpperCase();
}

@Component({
  selector: 'ui-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './avatar.component.html',
  styleUrl: './avatar.component.scss',
  host: {
    class: 'ui-avatar-host',
    '[class.ui-avatar-host--fill]': 'fill()',
  },
})
export class UiAvatarComponent {
  /** Imagen (URL); si existe, se muestra en lugar de icono e iniciales */
  readonly imageSrc = input<string | null>(null);

  /** Texto alternativo de la imagen (accesible) */
  readonly imageAlt = input('');

  /** Icono como imagen (URL), p. ej. SVG en /public */
  readonly iconSrc = input<string | null>(null);

  /** Texto para iniciales (sin imagen ni icono) */
  readonly label = input<string | null>(null);

  readonly size = input<UiAvatarSize>('md');
  /**
   * Estira el host al hueco del padre (aspect-ratio 1:1).
   * El tamaño sm|md|lg|xl no aplica; el inner llena el host.
   */
  readonly fill = input(false);
  readonly shape = input<UiAvatarShape>('circle');
  /** Fondo en modos label / icono */
  readonly tone = input<UiAvatarTone>('neutral');

  /** Badge numérico o texto (esquina superior derecha) */
  readonly badge = input<string | number | null>(null);

  readonly badgeTone = input<'danger' | 'neutral' | 'success'>('danger');

  /** Skeleton (sin imagen ni iniciales) */
  readonly loading = input(false);

  /** Texto accesible mientras `loading` es true */
  readonly loadingLabel = input('Cargando usuario');

  /**
   * Etiqueta accesible del avatar (rol img).
   * Si está vacío, se usa imageAlt, label o "Avatar".
   */
  readonly ariaLabel = input<string>('');

  protected readonly initials = computed(() => initialsFromLabel(this.label() ?? ''));

  protected readonly effectiveAriaLabel = computed(() => {
    if (this.loading()) return this.loadingLabel().trim() || 'Cargando';
    const explicit = this.ariaLabel().trim();
    if (explicit) return explicit;
    const alt = this.imageAlt().trim();
    if (alt) return alt;
    const lab = this.label()?.trim();
    if (lab) return lab;
    return 'Avatar';
  });

  protected readonly hasBadge = computed(() => {
    const b = this.badge();
    if (b === null || b === undefined) return false;
    if (typeof b === 'string' && b.trim() === '') return false;
    return true;
  });
}
